<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import {
  mdiAlertOutline,
  mdiChevronUp,
  mdiSpeedometer,
  mdiCheck,
  mdiAlertCircleOutline,
  mdiDownloadOutline,
  mdiCloudUploadOutline,
} from '@mdi/js'
import { useUploadStore } from '../../stores/useUploadStore'
import { useExportJobsStore } from '../../stores/useExportJobsStore'

const store = useUploadStore()
const exportJobs = useExportJobsStore()
const { t } = useI18n()
const { mobile } = useDisplay()

// === Uploads row ===
const hasActiveUploads = computed(() => store.hasActiveOrPendingUploads)
const uploadActiveCount = computed(() => store.overallProgress.activeCount)
const uploadTotalCount = computed(
  () => store.overallProgress.activeCount + store.overallProgress.pendingCount,
)
const uploadProgress = computed(() => store.overallProgress.totalProgress)

const formattedSpeed = computed(() => {
  const speed = store.uploadSpeed
  if (speed === 0) return null
  const k = 1024
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s']
  const i = Math.floor(Math.log(speed) / Math.log(k))
  const value = parseFloat((speed / Math.pow(k, i)).toFixed(1))
  return `${value} ${sizes[i]}`
})

// === Exports row ===
// Running exports take precedence; once everything has finished the row keeps
// the outcome on screen until the user acts on it, then disappears.
const exportState = computed<'running' | 'ready' | 'failed' | null>(() => {
  if (exportJobs.activeCount > 0) return 'running'
  if (exportJobs.readyPending.length > 0) return 'ready'
  if (exportJobs.failedPending.length > 0) return 'failed'
  return null
})

const singleReadyJob = computed(() =>
  exportJobs.readyPending.length === 1 ? (exportJobs.readyPending[0] ?? null) : null,
)

const exportTitle = computed(() => {
  switch (exportState.value) {
    case 'running':
      return t('assets.transfers.exports.inProgress', { count: exportJobs.activeCount }, exportJobs.activeCount)
    case 'ready':
      return singleReadyJob.value
        ? t('assets.transfers.exports.readyOne')
        : t('assets.transfers.exports.readyMany', { count: exportJobs.readyPending.length })
    case 'failed':
      return t(
        'assets.transfers.exports.failedCount',
        { count: exportJobs.failedPending.length },
        exportJobs.failedPending.length,
      )
    default:
      return ''
  }
})

/** Naming the tools involved is what tells the user everything lands here. */
const exportSubtitle = computed(() => {
  if (exportState.value === 'running') {
    const sources = exportJobs.activeKinds.map((kind) =>
      t(`assets.transfers.exports.source.${kind}`),
    )
    return `${sources.join(', ')} · ${exportJobs.averageProgress}%`
  }
  if (exportState.value === 'ready' && singleReadyJob.value) {
    return singleReadyJob.value.filename ?? singleReadyJob.value.label
  }
  if (exportState.value === 'failed') {
    return exportJobs.failedPending[0]?.label ?? ''
  }
  return ''
})

// Not on a phone, where it would sit on the bottom bar: Plus's badge counts what it shows, and
// the transfers line of Plus's sheet opens the drawer (docs/plans/mobile-navigation.md, lot 3).
const isVisible = computed(
  () => !mobile.value && (hasActiveUploads.value || exportState.value !== null) && !store.drawerOpen,
)

function openTab(tab: 'uploads' | 'exports') {
  store.drawerTab = tab
  store.openDrawer()
}

function downloadReady() {
  if (singleReadyJob.value) exportJobs.download(singleReadyJob.value.id)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div v-if="isVisible" class="upload-mini-dock">
        <div v-if="hasActiveUploads" class="upload-mini-dock__warning">
          <v-icon :icon="mdiAlertOutline" size="14" />
          <span>{{ t('assets.upload.miniDock.keepTabOpenWarning') }}</span>
        </div>

        <v-card class="upload-mini-dock__card" :elevation="4">
          <!-- The sweep signals work in flight; a finished export sits still. -->
          <div v-if="hasActiveUploads || exportState === 'running'" class="upload-mini-dock__pulse" />

          <!-- Uploads -->
          <div
            v-if="hasActiveUploads"
            class="upload-mini-dock__row"
            role="button"
            tabindex="0"
            @click="openTab('uploads')"
            @keydown.enter="openTab('uploads')"
            @keydown.space.prevent="openTab('uploads')"
          >
            <v-progress-circular
              :model-value="uploadProgress"
              :size="40"
              :width="4"
              color="primary"
              class="upload-mini-dock__progress"
            >
              <span class="text-caption font-weight-medium">{{ uploadTotalCount }}</span>
            </v-progress-circular>

            <div class="upload-mini-dock__info">
              <span class="text-body-2 font-weight-medium upload-mini-dock__title">
                <v-icon :icon="mdiCloudUploadOutline" size="14" class="mr-1" />
                {{ t('assets.upload.miniDock.uploading', { count: uploadActiveCount }, uploadActiveCount) }}
              </span>
              <div class="upload-mini-dock__details">
                <span class="text-caption text-medium-emphasis">{{ uploadProgress }}%</span>
                <span
                  v-if="formattedSpeed"
                  class="upload-mini-dock__speed text-caption text-medium-emphasis"
                >
                  <v-icon :icon="mdiSpeedometer" size="12" class="mr-1" />
                  {{ formattedSpeed }}
                </span>
              </div>
            </div>

            <v-icon :icon="mdiChevronUp" size="20" class="upload-mini-dock__chevron" />
          </div>

          <div
            v-if="hasActiveUploads && exportState"
            class="upload-mini-dock__separator"
          />

          <!-- Exports -->
          <div v-if="exportState" class="upload-mini-dock__row upload-mini-dock__row--exports">
            <div
              class="upload-mini-dock__main"
              role="button"
              tabindex="0"
              @click="openTab('exports')"
              @keydown.enter="openTab('exports')"
              @keydown.space.prevent="openTab('exports')"
            >
              <v-progress-circular
                v-if="exportState === 'running'"
                :model-value="exportJobs.averageProgress"
                :size="40"
                :width="4"
                color="primary"
                class="upload-mini-dock__progress"
              >
                <span class="text-caption font-weight-medium">{{ exportJobs.activeCount }}</span>
              </v-progress-circular>
              <div
                v-else
                class="upload-mini-dock__badge"
                :class="`upload-mini-dock__badge--${exportState}`"
              >
                <v-icon
                  :icon="exportState === 'ready' ? mdiCheck : mdiAlertCircleOutline"
                  size="20"
                />
              </div>

              <div class="upload-mini-dock__info">
                <span class="text-body-2 font-weight-medium upload-mini-dock__title">
                  <v-icon :icon="mdiDownloadOutline" size="14" class="mr-1" />
                  {{ exportTitle }}
                </span>
                <div class="upload-mini-dock__details">
                  <span class="text-caption text-medium-emphasis upload-mini-dock__subtitle">
                    {{ exportSubtitle }}
                  </span>
                </div>
              </div>

              <v-icon
                v-if="exportState === 'running'"
                :icon="mdiChevronUp"
                size="20"
                class="upload-mini-dock__chevron"
              />
            </div>

            <div v-if="exportState !== 'running'" class="upload-mini-dock__actions">
              <v-btn variant="text" size="small" @click.stop="exportJobs.acknowledgeAll">
                {{
                  exportState === 'ready'
                    ? t('assets.transfers.exports.later')
                    : t('assets.transfers.exports.dismiss')
                }}
              </v-btn>
              <v-btn
                v-if="singleReadyJob"
                color="primary"
                variant="flat"
                size="small"
                @click.stop="downloadReady"
              >
                {{ t('assets.transfers.exports.download') }}
              </v-btn>
              <v-btn v-else color="primary" variant="flat" size="small" @click.stop="openTab('exports')">
                {{ t('assets.transfers.exports.view') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.upload-mini-dock {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  width: 300px;

  &__warning {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 10px;
    background: rgb(var(--v-theme-warning));
    color: rgb(var(--v-theme-on-warning, 255, 255, 255));
    font-size: 11px;
    font-weight: 500;
    line-height: 1.35;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

    .v-icon {
      flex-shrink: 0;
      margin-top: 1px;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 12px;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;

    &:hover {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
  }

  &__pulse {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(var(--v-theme-primary), 0.08),
      transparent
    );
    animation: pulse-sweep 2s ease-in-out infinite;
    pointer-events: none;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
    z-index: 1;
    cursor: pointer;

    &--exports {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      cursor: default;
    }

    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: 3px;
      border-radius: 8px;
    }
  }

  &__main {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: 3px;
      border-radius: 8px;
    }
  }

  &__separator {
    height: 1px;
    background: rgba(var(--v-border-color), var(--v-border-opacity));
    margin: 0 -16px;
    position: relative;
    z-index: 1;
  }

  &__progress {
    flex-shrink: 0;
  }

  &__badge {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--ready {
      background: rgba(var(--v-theme-success), 0.12);
      color: rgb(var(--v-theme-success));
    }

    &--failed {
      background: rgba(var(--v-theme-error), 0.12);
      color: rgb(var(--v-theme-error));
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__title {
    display: inline-flex;
    align-items: center;
  }

  &__details {
    display: flex;
    align-items: center;
    gap: 8px;
    font-variant-numeric: tabular-nums;
    min-width: 0;
  }

  &__subtitle {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__speed {
    display: inline-flex;
    align-items: center;
    padding-left: 8px;
    border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }

  &__chevron {
    flex-shrink: 0;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }
}

@keyframes pulse-sweep {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(200%);
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@media (prefers-reduced-motion: reduce) {
  .upload-mini-dock__pulse {
    animation: none;
  }
}
</style>
